import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { UserCheck, ShieldCheck, FileCheck, CheckCircle, HelpCircle, ArrowRight, LayoutTemplate, FileText } from 'lucide-react';

// Asset Imports (Ensure these paths match your project structure)
import Dltimage from "../assets/dltregistration.jpg";
import Dltimage2 from "../assets/dlt2.webp";
import DltSideLogo from "../assets/dlt3.png";

// Premium 3D Text Style
const premium3DTextStyle = {
  textShadow: `
    0 1px 0 #ccc, 
    0 2px 0 #c9c9c9, 
    0 3px 0 #bbb, 
    0 4px 0 #b9b9b9, 
    0 5px 0 #aaa, 
    0 6px 1px rgba(0,0,0,.1), 
    0 0 5px rgba(0,0,0,.1), 
    0 1px 3px rgba(0,0,0,.3), 
    0 3px 5px rgba(0,0,0,.2), 
    0 5px 10px rgba(0,0,0,.25), 
    0 10px 10px rgba(0,0,0,.2), 
    0 20px 20px rgba(0,0,0,.15)
  `,
};

export const Dlt = () => {
  const navigate = useNavigate();

  // State for FAQ accordion
  const [openFaq, setOpenFaq] = useState(null);

  // --- CONFIGURATION DATA ---
  const steps = [
    {
      title: "Register as Entity",
      desc: "Sign up on any operator's DLT portal (Jio, Airtel, Vi, etc.) as a Principal Entity.",
      icon: <UserCheck size={28} />,
      color: "blue"
    },
    {
      title: "Upload Documents",
      desc: "Submit KYC documents including PAN, GST, Tan, and authorized signatory proof.",
      icon: <ShieldCheck size={28} />,
      color: "emerald"
    },
    {
      title: "Header Approval",
      desc: "Once verified, register your Sender IDs (Headers) that you intend to use for SMS.",
      icon: <LayoutTemplate size={28} />,
      color: "purple"
    },
    {
      title: "Content Templates",
      desc: "Upload your SMS content templates for approval. Link them to your Headers.",
      icon: <FileCheck size={28} />,
      color: "orange"
    },
    {
      title: "Go Live",
      desc: "Once approved, integrate your DLT credentials with the Eurofox platform to start sending.",
      icon: <CheckCircle size={28} />,
      color: "teal"
    },
  ];

  const docsList = [
    { title: "Business PAN Card", color: "blue" },
    { title: "GST Incorporation Certificate", color: "emerald" },
    { title: "Authorized Signatory Aadhar/PAN", color: "purple" },
    { title: "Authorization Letter on Letterhead", color: "orange" },
    { title: "Proof of Address", color: "teal" }
  ];

  const dltFaqs = [
    {
      question: "What is DLT in the context of SMS?",
      answer: "Distributed Ledger Technology (DLT) is a block-chain based registration system mandated by TRAI. It maintains a record of all entities (businesses), headers (sender IDs), and templates to ensure transparency and prevent spam."
    },
    {
      question: "How much time does DLT registration take?",
      answer: "Entity registration usually takes 48-72 hours. Header and Template approvals vary by operator but generally take another 2-3 business days if all documents are correct."
    },
    {
      question: "Can I use one DLT registration for multiple operators?",
      answer: "Yes. Once you register as a Principal Entity (PE) on one operator's portal (e.g., Jio), you can 'cross-register' that same PE ID on other operators like Airtel or Vodafone Idea."
    },
    {
      question: "Are there any charges for DLT registration?",
      answer: "Most telecom operators charge a one-time registration fee of approximately ₹5,000 + GST. However, fees may vary or be waived during promotional periods by specific operators."
    },
    {
      question: "What is the difference between Service Implicit and Service Explicit?",
      answer: "Implicit templates are for transactional messages like OTPs and order updates. Explicit templates are for promotional content where customers have given consent to receive marketing messages."
    }
  ];

  // Helper for Theme Colors
  const getTheme = (color) => {
    const themes = {
      blue: {
        bg: "bg-gradient-to-br from-blue-100 via-blue-50 to-cyan-100",
        iconBg: "bg-gradient-to-br from-blue-600 to-cyan-500",
        border: "border-blue-200 ring-1 ring-blue-100",
        text: "text-blue-800",
        hoverBorder: "group-hover:border-blue-300"
      },
      emerald: {
        bg: "bg-gradient-to-br from-emerald-100 via-emerald-50 to-green-100",
        iconBg: "bg-gradient-to-br from-emerald-600 to-green-500",
        border: "border-emerald-200 ring-1 ring-emerald-100",
        text: "text-emerald-800",
        hoverBorder: "group-hover:border-emerald-300"
      },
      purple: {
        bg: "bg-gradient-to-br from-purple-100 via-purple-50 to-pink-100",
        iconBg: "bg-gradient-to-br from-purple-600 to-pink-500",
        border: "border-purple-200 ring-1 ring-purple-100",
        text: "text-purple-800",
        hoverBorder: "group-hover:border-purple-300"
      },
      orange: {
        bg: "bg-gradient-to-br from-orange-100 via-orange-50 to-amber-100",
        iconBg: "bg-gradient-to-br from-orange-600 to-amber-500",
        border: "border-orange-200 ring-1 ring-orange-100",
        text: "text-orange-800",
        hoverBorder: "group-hover:border-orange-300"
      },
      teal: {
        bg: "bg-gradient-to-br from-teal-100 via-teal-50 to-emerald-100",
        iconBg: "bg-gradient-to-br from-teal-600 to-emerald-500",
        border: "border-teal-200 ring-1 ring-teal-100",
        text: "text-teal-800",
        hoverBorder: "group-hover:border-teal-300"
      }
    };
    return themes[color] || themes.blue;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 50, damping: 15 } }
  };

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900 overflow-x-hidden">

      {/* ================= HERO SECTION ================= */}
      <div className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
        <div className="absolute inset-0 bg-gray-900">
          <img
            src={Dltimage}
            alt="DLT Registration Hero"
            className="w-full h-full object-cover blur-[1px] scale-105 opacity-70"
          />
          <div className="absolute inset-0 bg-black/10"></div>
        </div>
        <div className="relative z-10 h-full flex flex-col justify-center items-center px-4">
          <h1
            className="text-4xl md:text-8xl font-extrabold tracking-tight text-white uppercase text-center"
            style={premium3DTextStyle}
          >
            DLT REGISTRATION
          </h1>
        </div>
      </div>

      {/* ================= SECTION 1: MANDATORY NOTICE ================= */}
      <section className="pt-16 pb-6 px-4 md:px-12 lg:px-20 max-w-[1400px] mx-auto ">
        <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-20">
          <div className="w-full lg:w-3/4 space-y-6">
            <h2 className="text-3xl lg:text-4xl xl:text-4xl font-bold text-gray-900 tracking-tight leading-tight lg:whitespace-nowrap">
              DLT Registration is Mandatory for <span className="text-[#F46653]">SMS Services</span> in India
            </h2>
            <div className="max-w-4xl">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                To curb unsolicited commercial communication (UCC), the Telecom Regulatory Authority of India (TRAI) has mandated the use of Distributed Ledger Technology (DLT).
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Every business must register as a Principal Entity (PE). Failure to comply results in immediate message blocking. At Eurofox, we streamline this entire compliance journey for you.
              </p>
            </div>
            <div className="pt-2">
              <button className="px-8 py-3.5 bg-[#0C4550] text-white font-medium rounded-lg hover:bg-[#09353e] transition-all duration-300 shadow-sm hover:shadow-md flex items-center gap-2">
                Start Registration
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
              </button>
            </div>
          </div>
          <div className="w-full lg:w-1/4 hidden md:flex justify-center lg:justify-end items-start relative">
            <div className="relative w-20 h-22 -left-6 z-10 flex-shrink-0 mt-24">
              <img src={DltSideLogo} alt="Compliance Seal" className="w-full h-full object-contain" />
            </div>
            <div className="relative w-64 md:w-72 h-84 top-10 z-0 transform scale-125 origin-top">
              <img src={Dltimage2} alt="DLT Registration Process" className="w-full h-full object-contain" />
            </div>
          </div>
        </div>
      </section>

      {/* ================= SECTION 2: STEPS TO REGISTER ================= */}
      <section className="pt-16 pb-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-50/50 rounded-full blur-3xl mix-blend-multiply"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-50/50 rounded-full blur-3xl mix-blend-multiply"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-extrabold text-black mb-6 tracking-tight">
                Steps to Register for <span className="text-[#F46653]">DLT</span>
              </h2>
              <p className="text-gray-700 max-w-2xl mx-auto text-medium font-medium">
                Follow this simplified roadmap to get your Sender IDs and Templates approved quickly.
              </p>
            </motion.div>
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {steps.map((step, index) => {
              const theme = getTheme(step.color);
              return (
                <motion.div key={index} variants={cardVariants} className="relative group h-full">
                  <div className={`relative h-full rounded-2xl ${theme.bg} ${theme.border} shadow-sm hover:shadow-xl backdrop-blur-md overflow-hidden p-8 transition-all duration-300 hover:-translate-y-2`}>
                    <span className="absolute -right-4 -top-6 text-[9rem] font-bold opacity-10 select-none pointer-events-none text-black group-hover:opacity-20 transition-opacity">
                      {index + 1}
                    </span>
                    <div className="relative z-10">
                      <div className="mb-6 relative">
                        <div className={`absolute inset-0 w-14 h-14 rounded-xl ${theme.iconBg} opacity-30 blur-lg scale-110 group-hover:scale-125 transition-transform duration-300`} />
                        <div className={`relative w-14 h-14 rounded-xl ${theme.iconBg} flex items-center justify-center text-white shadow-md ring-1 ring-white/30`}>
                          {step.icon}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 bg-white/70 rounded-md backdrop-blur-sm border border-white/60 text-slate-700 shadow-sm">
                          Step {index + 1}
                        </span>
                        <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
                      </div>
                      <p className="text-gray-700 leading-relaxed text-[15px] font-medium">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}

            <motion.div
              variants={cardVariants}
              whileHover={{ scale: 1.02 }}
              className="relative rounded-2xl shadow-xl flex flex-col justify-center items-center text-center overflow-hidden group h-full"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#0C4550] to-[#062a31] z-0"></div>
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#F46653] rounded-full blur-[80px] opacity-10 group-hover:opacity-20 transition-opacity duration-500 z-0 translate-x-1/2 -translate-y-1/2"></div>

              <div className="relative z-10 w-full p-8">
                <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6 backdrop-blur-md border border-white/10">
                  <HelpCircle className="text-white" size={28} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Need Help?</h3>
                <p className="text-gray-300 mb-8 text-sm leading-relaxed px-2">
                  Our support team can handle the DLT documentation process for you.
                </p>
                <button
                  onClick={() => navigate('/contact')}
                  className="group flex items-center justify-center gap-2 w-full px-6 py-3 bg-[#F46653] text-white rounded-lg font-semibold hover:bg-white hover:text-black transition-all duration-300 "
                >
                  <span>Contact Support</span>
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ================= SECTION 3: DOCUMENTS REQUIRED ================= */}
      <section className="pt-0 pb-20 px-4 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row gap-12">
          <div className="w-full md:w-1/3">
            <div className="sticky top-24">
              <span className="text-[#F46653] font-bold tracking-wider uppercase text-sm mb-2 block">CHECKLIST</span>
              <h2 className="text-4xl font-bold text-black mb-6">Documents Required</h2>
              <p className="text-gray-700 mb-8 text-lg leading-relaxed">
                Ensure you have digital copies of these documents ready before initiating the registration process on any operator portal.
              </p>
              <div className="h-1.5 w-24 bg-[#F46653] rounded-full"></div>
            </div>
          </div>

          <div className="w-full md:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {docsList.map((doc, idx) => {
              const theme = getTheme(doc.color);
              return (
                <div key={idx} className={`group relative flex items-center p-5 rounded-2xl bg-white border border-gray-200 shadow-sm transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${theme.hoverBorder}`}>
                  <div className="relative z-10 flex items-center space-x-5 w-full">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-xl ${theme.iconBg} flex items-center justify-center text-white shadow-md ring-2 ring-white`}>
                      <FileText size={22} />
                    </div>
                    <span className="font-semibold text-lg text-gray-800 group-hover:text-black transition-colors">
                      {doc.title}
                    </span>
                  </div>
                  <div className="absolute right-4 top-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <CheckCircle size={18} className="text-[#F46653]" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= SECTION 5: FREQUENTLY ASKED QUESTIONS ================= */}
      <section className="bg-white w-full py-16 md:py-24 px-6 md:px-12 lg:px-20 -mt-18">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-start justify-between gap-12">

          {/* Left Side: Heading with Animated Underline */}
          <div className="w-full lg:w-1/3">
            <div className="relative inline-block group">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                Frequently <br className="hidden lg:block" /> asked questions
              </h2>
              {/* ANIMATED UNDERLINE using your brand color #F46653 */}
              <span className="absolute left-0 -bottom-2 w-1/4 h-1.5 bg-[#F46653] rounded-full transition-all duration-500 group-hover:w-full"></span>
            </div>
            <p className="mt-8 text-gray-600 text-lg max-w-sm">
              Everything you need to know about TRAI compliance and the registration process.
            </p>
          </div>

          {/* Right Side: Professional Accordion Questions */}
          <div className="w-full lg:w-2/3 space-y-4">
            {dltFaqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl overflow-hidden transition-all duration-300 shadow-sm border border-gray-200 hover:border-[#0C4550]/30 hover:shadow-md"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 md:p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-base md:text-lg font-semibold leading-snug text-gray-800">
                    {faq.question}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2.5}
                    stroke="currentColor"
                    className={`w-5 h-5 transition-transform duration-300 ${openFaq === index ? 'rotate-180 text-[#F46653]' : 'text-gray-400'}`}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                  </svg>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-500 ease-in-out ${openFaq === index ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                >
                  <div className="p-6 pt-0 text-gray-600 leading-relaxed border-t border-gray-50 text-[15px]">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>
    </div>
  );
};